// TEAMS Website - HeroSection Component
// Full-viewport hero section with tagline and CTA

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useScrollReveal } from '../../hooks/useScrollReveal.js';
import { getSlideshowImagePath } from '../../utils/assetPath.js';
import Button from '../ui/Button.jsx';
import '../../styles/main.scss';

const HeroSection = () => {
  const { t } = useTranslation();
  const [isLoaded, setIsLoaded] = useState(false);
  const [ref, isVisible] = useScrollReveal({ threshold: 0.1, delay: 200 });
  
  // Slideshow state
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [slideImages, setSlideImages] = useState([]);
  const [imagesLoaded, setImagesLoaded] = useState(false);

  // Dynamic slideshow images from public/main-slideshow
  useEffect(() => {
    // In production, you might want to fetch this from an API endpoint
    // For now, we'll define the images that exist in the folder
    const images_v1 = [
      'DSCN1543.JPG',
      'DSCN1669.JPG', 
      'DSCN1695.JPG',
      'DSCN2562.JPG',
      'DSCN2573.JPG',
      'IMG_20170323_195928.jpg',
      'IMG_20170323_213320.jpg',
      'IMG_20170324_214133.jpg',
      'IMG_20170409_181951.jpg',
      'IMG_20170514_175612.jpg',
      'IMG_20170514_184652.jpg',
      'IMG_20180615_213642.jpg',
      'IMG_6128.JPG',
      'IMG_6207.JPG',
      'IMG_6214.JPG',
      'IMG_6215.JPG',
      'andrianivilla.jpg',
      'italicosplendoreteam.jpg',
      'italicosplendorevilla.jpg',
      'italicovestiti.jpg',
      'prina.jpg',
      'ravenna2018.jpg'
    ];
     const images = [
     // 'IMG_6128.JPG',
     // 'DSCN1577.JPG',
     // 'IMG_20180615_213642.jpg',
     // 'IMG_20170323_195928.jpg',
     // 'alcapicla.jpg',
      'claudio-alcapicla.jpg',
     // 'claudio-prina.jpg',
     // 'niguntrio.jpg',
     // 'pasqua2018.jpg',
    //  'quartettoandriani.jpeg',
    //  'tehilaclaudio.jpg',
     ];
    
    // Randomize the image order
    const shuffledImages = [...images].sort(() => Math.random() - 0.5);
    setSlideImages(shuffledImages);
    
    // Preload images
    const preloadPromises = images.map(src => {
      return new Promise((resolve) => {
        const img = new Image();
        img.onload = resolve;
        img.src = getSlideshowImagePath(src);
      });
    });
    
    Promise.all(preloadPromises).then(() => {
      setImagesLoaded(true);
    });
  }, []);

  // Auto-advance slideshow
  useEffect(() => {
    if (slideImages.length <= 1) return;
    
    const interval = setInterval(() => {
      setCurrentImageIndex((prev) => (prev + 1) % slideImages.length);
    }, 8000); // Change image every 8 seconds for gentle transitions

    return () => clearInterval(interval);
  }, [slideImages.length]);

  // Trigger animation on mount
  useEffect(() => {
    setIsLoaded(true);
  }, []);

  return (
    <section className="hero-section">
      {/* Background */}
      <div className="hero-section__background">
        <div className="hero-section__overlay"></div>
        {/* Dynamic slideshow */}
        <div className="hero-section__slideshow">
          {slideImages.map((image, index) => (
            <div
              key={image}
              className={`hero-section__slide ${index === currentImageIndex ? 'hero-section__slide--active' : ''}`}
              style={{
                opacity: index === currentImageIndex ? 1 : 0,
                transform: `scale(${index === currentImageIndex ? 1 : 1.1}) translateX(${index === currentImageIndex ? 0 : index < currentImageIndex ? '-5%' : '5%'})`,
                transition: 'opacity 4s ease-in-out, transform 8s ease-in-out'
              }}
            >
              <img 
                src={getSlideshowImagePath(image)}
                alt={`TEAMS Hero Slide ${index + 1}`}
                style={{ 
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover'
                }}
              />
            </div>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="hero-section__content">
        <div className="hero-section__container">
          <div 
            ref={ref}
            className={`hero-section__text ${isVisible ? 'hero-section__text--visible' : ''}`}
          >
            {/* Main title */}
            <h1 className="hero-section__title">
              <span className="hero-section__title-main">TEAMS</span>
              <span className="hero-section__title-tagline">
                {t('hero.tagline')}
              </span>
            </h1>

            {/* Subtitle 
            <p className="hero-section__subtitle">
              {t('hero.subtitle')}
            </p>*/}

            {/* Call to action */}
            <div className="hero-section__cta">
              <a href="/#booking">
                <Button variant="primary" size="large" className="hero-section__button">
                  {t('hero.cta')}
                </Button>
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Animated background elements */}
      <div className="hero-section__particles">
        {[...Array(20)].map((_, index) => (
          <div
            key={index}
            className={`hero-section__particle hero-section__particle--${index + 1}`}
            style={{
              animationDelay: `${index * 0.2}s`,
              left: `${Math.random() * 100}%`,
              animationDuration: `${3 + Math.random() * 4}s`
            }}
          />
        ))}
      </div>
    </section>
  );
};

export default HeroSection;
